import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import {
  CheckCircle2, XCircle, Minus, Lock, Clock, ShieldCheck, Send, Sparkles,
} from "lucide-react";
import { API } from "../utils/qccaApi";

export default function QCCABallot() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState(null); // {vote, ballot}
  const [choice, setChoice] = useState("");
  const [unitConfirm, setUnitConfirm] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);

  // Post-vote CYQR opt-in
  const [showOptIn, setShowOptIn] = useState(false);
  const [optEmail, setOptEmail] = useState("");
  const [optName, setOptName] = useState("");
  const [optMarketing, setOptMarketing] = useState(false);
  const [optCouncillor, setOptCouncillor] = useState(false);
  const [optSubmitting, setOptSubmitting] = useState(false);
  const [optDone, setOptDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/qcca/vote/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const deadlinePassed = useMemo(() => {
    if (!data?.vote?.deadline_iso) return false;
    return new Date(data.vote.deadline_iso).getTime() < Date.now();
  }, [data]);

  const submitBallot = async (e) => {
    e.preventDefault();
    if (!choice) { toast.error("Please pick Yes, No, or Abstain."); return; }
    if (!unitConfirm.trim()) { toast.error("Please enter your unit number."); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/qcca/vote/${token}`, {
        choice, unit_confirmation: unitConfirm.trim(), comment: comment.trim() || null,
      });
      setReceipt(res.data.receipt);
      setShowOptIn(true);
      toast.success("Your vote has been recorded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit vote.");
    } finally { setBusy(false); }
  };

  const submitOptIn = async (e) => {
    e.preventDefault();
    if (!optMarketing && !optCouncillor) {
      toast.error("Please tick at least one consent to sign up.");
      return;
    }
    setOptSubmitting(true);
    try {
      await axios.post(`${API}/qcca/opt-in`, {
        email: optEmail.trim().toLowerCase(),
        name: optName.trim() || null,
        consent_marketing: optMarketing,
        consent_councillor_contact: optCouncillor,
        source: "qcca_post_vote",
        ballot_token: token,
      });
      setOptDone(true);
      toast.success("Thanks — your preferences are saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save preferences.");
    } finally { setOptSubmitting(false); }
  };

  // Not found
  if (notFound) {
    return (
      <Shell>
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-10 max-w-lg mx-auto text-center" data-testid="qcca-ballot-not-found">
          <XCircle className="w-12 h-12 text-red-400 mx-auto" />
          <h1 className="mt-6 font-headline text-3xl text-white">Ballot not found.</h1>
          <p className="mt-3 text-gray-400 text-sm">This link may have expired, been rescinded, or already been used. If you believe this is a mistake, contact your condo board.</p>
          <Link to="/" className="mt-8 inline-flex font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5A52A] hover:text-white">
            ← Learn about QCCA
          </Link>
        </div>
      </Shell>
    );
  }

  if (loading || !data) return <Shell><div className="text-gray-500 font-mono text-sm text-center py-20">Loading your ballot…</div></Shell>;

  // Already voted (either on load or just now)
  const alreadyCast = data.ballot.already_voted && !receipt;

  return (
    <Shell>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 border border-[#C5A52A]/40 text-[#C5A52A] font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5">
            <ShieldCheck className="w-3 h-3" /> QCCA Secure Ballot
          </div>
          <h1 className="mt-5 font-headline text-3xl sm:text-4xl text-white leading-tight">{data.vote.title}</h1>
          <div className="mt-2 font-mono text-[11px] text-gray-500 uppercase tracking-[0.22em]">
            {data.vote.corporation}
          </div>
          <p className="mt-5 text-gray-300 leading-relaxed whitespace-pre-wrap">{data.vote.description}</p>
          <div className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
            <Clock className="w-3 h-3 text-[#C5A52A]" /> Closes {new Date(data.vote.deadline_iso).toLocaleString()}
          </div>
        </div>

        {alreadyCast && (
          <div className="bg-[#0a0a0a] border border-[#C5A52A]/50 p-8" data-testid="qcca-ballot-already-cast">
            <CheckCircle2 className="w-10 h-10 text-[#C5A52A]" />
            <h2 className="mt-4 font-headline text-2xl text-white">This ballot has already been submitted.</h2>
            <p className="mt-2 text-gray-400 text-sm">Each ballot link can only be used once. If you believe this is in error, contact your condo board.</p>
          </div>
        )}

        {deadlinePassed && !receipt && !alreadyCast && (
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-8" data-testid="qcca-ballot-closed">
            <Lock className="w-10 h-10 text-gray-500" />
            <h2 className="mt-4 font-headline text-2xl text-white">Voting has closed.</h2>
            <p className="mt-2 text-gray-400 text-sm">The deadline has passed. Contact your board for information on the results.</p>
          </div>
        )}

        {/* Ballot form */}
        {!alreadyCast && !deadlinePassed && !receipt && (
          <form onSubmit={submitBallot} className="bg-[#0a0a0a] border border-[#1a1a1a] p-6 sm:p-10 space-y-6" data-testid="qcca-ballot-form">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-4">Your vote</div>
              <div className="grid sm:grid-cols-3 gap-3">
                {[
                  { v: "yes", label: "Yes", icon: CheckCircle2, tone: "border-[#C5A52A] text-[#C5A52A]" },
                  { v: "no", label: "No", icon: XCircle, tone: "border-gray-500 text-gray-200" },
                  { v: "abstain", label: "Abstain", icon: Minus, tone: "border-gray-700 text-gray-400" },
                ].map((opt) => {
                  const Ico = opt.icon;
                  const selected = choice === opt.v;
                  return (
                    <label key={opt.v}
                      className={`cursor-pointer border p-5 flex flex-col items-center justify-center gap-2 transition-colors ${
                        selected ? `${opt.tone} bg-[#C5A52A]/5 ring-1 ring-[#C5A52A]/30` : "border-[#1a1a1a] text-gray-400 hover:border-[#C5A52A]/40 hover:text-white"
                      }`}
                      data-testid={`qcca-ballot-choice-${opt.v}`}>
                      <input type="radio" name="choice" className="sr-only" checked={selected} onChange={() => setChoice(opt.v)} />
                      <Ico className="w-8 h-8" />
                      <span className="font-headline text-2xl">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">
                Confirm your unit number *
              </label>
              <input
                value={unitConfirm}
                onChange={(e) => setUnitConfirm(e.target.value)}
                placeholder="e.g. 101"
                className="w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-white placeholder-gray-600 font-mono text-sm px-4 py-3 outline-none transition-colors"
                data-testid="qcca-ballot-unit"
              />
              <p className="mt-2 font-mono text-[10px] text-gray-600 leading-relaxed">
                For security, we verify your unit number at submission rather than displaying it on the ballot. Enter it exactly as it appears on your condo statement.
              </p>
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">
                Optional comment (visible to the board only)
              </label>
              <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="Anything the board should know about your vote?"
                className="w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-white placeholder-gray-600 font-mono text-sm px-4 py-3 outline-none transition-colors resize-none"
                data-testid="qcca-ballot-comment" />
            </div>

            <button type="submit" disabled={busy || !choice}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#C5A52A] disabled:bg-[#1a1a1a] disabled:text-gray-500 hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-6 py-4 transition-colors"
              data-testid="qcca-ballot-submit">
              <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit my ballot"}
            </button>
          </form>
        )}

        {/* Confirmation + opt-in */}
        {receipt && (
          <>
            <div className="bg-[#0a0a0a] border border-[#C5A52A]/60 p-8 text-center" data-testid="qcca-ballot-receipt">
              <CheckCircle2 className="w-14 h-14 text-[#C5A52A] mx-auto" />
              <h2 className="mt-5 font-headline text-3xl text-white">Your vote is counted.</h2>
              <p className="mt-2 text-gray-400 text-sm">
                <span className="text-white">{receipt.vote_title}</span> · you voted <span className="text-[#C5A52A] uppercase">{receipt.choice}</span> · recorded {new Date(receipt.voted_at).toLocaleString()}
              </p>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 break-all">
                Receipt ID: {receipt.receipt_id}
              </div>
            </div>

            {/* CYQR opt-in */}
            {showOptIn && !optDone && (
              <div className="mt-8 bg-[#050505] border border-[#1a1a1a] p-6 sm:p-8" data-testid="qcca-optin-panel">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center border border-[#C5A52A]/40 text-[#C5A52A] shrink-0">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Optional</div>
                    <h3 className="mt-2 font-headline text-2xl text-white leading-tight">
                      Want a seat at a bigger table?
                    </h3>
                    <p className="mt-2 text-gray-400 text-sm leading-relaxed">
                      QCCA is powered by <span className="text-white">CYQR.ca</span> — Regina's civic engagement platform. If you'd like to receive civic updates or hear directly from your city councillor about things that affect condo owners, opt in below. Your preferences are stored under Saskatchewan / PIPEDA-aligned consent rules, and you can withdraw at any time.
                    </p>
                  </div>
                </div>

                <form onSubmit={submitOptIn} className="mt-6 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input type="email" required value={optEmail} onChange={(e) => setOptEmail(e.target.value)}
                      placeholder="Your email"
                      className="w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-white placeholder-gray-600 font-mono text-sm px-4 py-3 outline-none transition-colors"
                      data-testid="qcca-optin-email" />
                    <input value={optName} onChange={(e) => setOptName(e.target.value)}
                      placeholder="Your name (optional)"
                      className="w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-white placeholder-gray-600 font-mono text-sm px-4 py-3 outline-none transition-colors" />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={optMarketing} onChange={(e) => setOptMarketing(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#C5A52A]" data-testid="qcca-optin-marketing" />
                    <span className="text-xs text-gray-400 leading-relaxed">
                      I consent to receive civic updates and announcements from CYQR.ca and QCCA. I understand my email will be used only for this purpose and that I can withdraw consent any time by emailing <span className="text-gray-300">privacy@cyqr.ca</span>.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={optCouncillor} onChange={(e) => setOptCouncillor(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#C5A52A]" data-testid="qcca-optin-councillor" />
                    <span className="text-xs text-gray-400 leading-relaxed">
                      I consent to my city councillor being able to contact me directly about matters affecting condo owners in my ward.
                    </span>
                  </label>

                  <button type="submit" disabled={optSubmitting || (!optMarketing && !optCouncillor)}
                    className="inline-flex items-center gap-2 bg-[#C5A52A] disabled:bg-[#1a1a1a] disabled:text-gray-500 hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors"
                    data-testid="qcca-optin-submit">
                    <Sparkles className="w-4 h-4" /> {optSubmitting ? "Saving…" : "Save my preferences"}
                  </button>

                  <button type="button" onClick={() => setShowOptIn(false)}
                    className="ml-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 hover:text-[#C5A52A]"
                    data-testid="qcca-optin-skip">
                    No thanks, I'm good
                  </button>
                </form>
              </div>
            )}

            {optDone && (
              <div className="mt-8 bg-[#050505] border border-[#C5A52A]/40 p-6 text-center" data-testid="qcca-optin-done">
                <CheckCircle2 className="w-10 h-10 text-[#C5A52A] mx-auto" />
                <h3 className="mt-3 font-headline text-xl text-white">You're on the list.</h3>
                <p className="mt-1 text-gray-500 text-xs">We'll honour your consent choices and never sell your data.</p>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black text-white relative z-10">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {children}
        <div className="mt-14 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-gray-600">
          Queen City Condo Association · Regina
        </div>
      </div>
    </div>
  );
}
